/* eslint-disable react-refresh/only-export-components */
import React, { useEffect, useState, useMemo, useRef, useCallback, createContext, useContext } from 'react';
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useTutorial } from '../hooks/useTutorial';
import { tutorialSteps } from './tutorialSteps';
import { showTutorialWelcome, showTutorialComplete, showTutorialMessage } from '../utils/alertUtils';
import { DASHBOARD_TABS, type DashboardTab } from '../types/dashboard';
import { useDevModeStore, selectDisableTutorial } from '../stores/devModeStore';

interface TutorialContextValue {
  isRunning: boolean;
}

const TutorialContext = createContext<TutorialContextValue>({ isRunning: false });

interface TutorialProviderProps {
  children: React.ReactNode;
  currentTab?: DashboardTab;
  onTabChange?: (tab: DashboardTab) => void;
  onResumeGame?: () => void;
  /** When true, suppresses the auto-welcome dialog (used when loading a saved game) */
  isLoadedGame?: boolean;
}

export const useTutorialContext = () => useContext(TutorialContext);

export function TutorialProvider({ children, currentTab, onTabChange, onResumeGame, isLoadedGame = false }: TutorialProviderProps) {
  const disableTutorial = useDevModeStore(selectDisableTutorial);
  const {
    tutorialState,
    isRunning,
    stepIndex,
    currentStep,
    startTutorial,
    skipTutorial,
    completeTutorial,
    handleJoyrideCallback
  } = useTutorial();

  const [showWelcome, setShowWelcome] = useState(false);
  const [run, setRun] = useState(false);
  const [isChangingTab, setIsChangingTab] = useState(false);
  const tabChangeTimeoutRef = useRef<NodeJS.Timeout>();
  const safetyTimeoutRef = useRef<NodeJS.Timeout>();

  // Helper function to handle tutorial completion (DRY)
  const handleTutorialComplete = useCallback(() => {
    completeTutorial();
    setRun(false);
    showTutorialComplete();
    if (onTabChange) {
      onTabChange(DASHBOARD_TABS.HEADQUARTERS);
    }
    if (onResumeGame) {
      onResumeGame();
    }
  }, [completeTutorial, onTabChange, onResumeGame]);

  // Check if we should show welcome on mount or when tutorial state changes
  useEffect(() => {
    // Don't auto-show welcome for loaded games or if tutorial is disabled in dev mode
    if (isLoadedGame || disableTutorial) return;

    // Only show if not completed and not skipped and not already running
    if (!tutorialState.isCompleted && !tutorialState.isSkipped && !isRunning && !showWelcome) {
      // Small delay to let the game dashboard render
      const timer = setTimeout(() => {
        setShowWelcome(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [tutorialState.isCompleted, tutorialState.isSkipped, isRunning, showWelcome, isLoadedGame, disableTutorial]);

  // Show welcome dialog
  useEffect(() => {
    if (showWelcome) {
      showTutorialWelcome().then((result) => {
        if (result.isConfirmed) {
          // Game is already paused by default, just start tutorial
          startTutorial();
          setRun(true);
        } else {
          skipTutorial();
          showTutorialMessage(
            'You can always access help through the menu if you need it later.',
            'Tutorial Skipped'
          );
          // Resume game when tutorial is skipped
          if (onResumeGame) {
            onResumeGame();
          }
        }
        setShowWelcome(false);
      });
    }
  }, [showWelcome, startTutorial, skipTutorial, onResumeGame]);

  // Sync running state
  useEffect(() => {
    setRun(isRunning);
  }, [isRunning]);

  // Safety timeout to ensure isChangingTab doesn't get stuck
  useEffect(() => {
    if (isChangingTab) {
      // Clear any existing safety timeout
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
      }

      // Set a maximum timeout for tab changes
      safetyTimeoutRef.current = setTimeout(() => {
        console.warn('Tutorial: Tab change took too long, resetting isChangingTab');
        setIsChangingTab(false);
      }, 1000); // 1 second maximum for any tab change
    } else {
      // Clear safety timeout if we're not changing tabs
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
      }
    }

    return () => {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
      }
    };
  }, [isChangingTab]);

  // Handle tab activation when step changes (only when tutorial is running)
  useEffect(() => {
    if (isRunning && currentStep && currentStep.tabToActivate && onTabChange && !isChangingTab) {
      // Only trigger tab change if we're not already on the target tab
      if (currentTab !== currentStep.tabToActivate) {
        // Clear any existing timeout
        if (tabChangeTimeoutRef.current) {
          clearTimeout(tabChangeTimeoutRef.current);
        }

        setIsChangingTab(true);
        onTabChange(currentStep.tabToActivate);

        // Add delay to ensure DOM is ready after tab change
        tabChangeTimeoutRef.current = setTimeout(() => {
          setIsChangingTab(false);
        }, 300);
      }
    }

    return () => {
      if (tabChangeTimeoutRef.current) {
        clearTimeout(tabChangeTimeoutRef.current);
      }
    };
  }, [isRunning, currentStep, currentTab, onTabChange, isChangingTab]);

  // Process steps for Joyride
  const processedSteps = useMemo(() => {
    return tutorialSteps.map(step => {
      const { ...joyrideStep } = step;
      return {
        ...joyrideStep,
        disableBeacon: true,
        showProgress: true,
        showSkipButton: true,
        styles: {
          options: {
            zIndex: 10000,
            primaryColor: '#10b981',
            textColor: '#1f2937',
            backgroundColor: '#fff',
            arrowColor: '#fff',
            overlayColor: 'rgba(0, 0, 0, 0.5)',
          },
          tooltip: {
            fontSize: 14,
            padding: 16,
          },
          buttonNext: {
            backgroundColor: '#10b981',
            color: '#fff',
            borderRadius: 4,
            padding: '8px 16px',
          },
          buttonBack: {
            color: '#6b7280',
            marginRight: 8,
          },
          buttonSkip: {
            color: '#ef4444',
          },
          buttonClose: {
            display: 'none',
          },
          tooltipContent: {
            textAlign: 'left' as const,
          }
        },
      };
    });
  }, []);

  // Enhanced callback handler
  const handleCallback = (data: CallBackProps) => {
    const { status, action, index, type } = data;

    // Handle errors
    if (type === EVENTS.ERROR) {
      const nextStep = tutorialSteps[index + 1];
      if (nextStep) {
        console.warn(`Tutorial: Could not find target for step ${index}, attempting to continue...`);
        // Try to continue to next step after a delay
        setTimeout(() => {
          if (nextStep.tabToActivate && onTabChange) {
            onTabChange(nextStep.tabToActivate);
          }
        }, 500);
      }
      return;
    }

    // Check if this is the last step
    if (index === tutorialSteps.length - 1 && action === ACTIONS.NEXT) {
      handleTutorialComplete();
      return;
    }

    // Handle status changes
    if (status === STATUS.FINISHED) {
      handleTutorialComplete();
      return;
    }

    if (status === STATUS.SKIPPED) {
      skipTutorial();
      setRun(false);
      showTutorialMessage(
        'You can always access help through the menu if you need it later.',
        'Tutorial Skipped'
      );
      // Resume game when tutorial is skipped
      if (onResumeGame) {
        onResumeGame();
      }
      return;
    }

    // Handle step changes - removed proactive tab switching
    // Tab changes are now handled by the useEffect watching currentStep

    // Pass to hook handler for step management
    handleJoyrideCallback(data);
  };

  return (
    <TutorialContext.Provider value={{ isRunning }}>
      {children}
      <Joyride
        steps={processedSteps}
        run={run && !isChangingTab}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        callback={handleCallback}
        scrollToFirstStep={false}
        scrollOffset={100}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip Tutorial',
        }}
        floaterProps={{
          disableAnimation: false,
        }}
        spotlightPadding={8}
        disableOverlayClose={true}
        disableCloseOnEsc={true}
        spotlightClicks={false}
      />
    </TutorialContext.Provider>
  );
}